import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import axiosApi from '../server/service';
import toast from 'react-hot-toast';


export interface GroupMember {
  _id: string;
  name: string;
  activeProgressId?: string | null;
}


export interface IDailyProgress {
  _id: string;
  memberId: string;
  memberName: string;
  piecesClaimed: number;
  dateClaimed: string;
  status: 'In Progress' | 'Completed';
  dateCompleted?: string;
}

export interface IGroupWorkAssignment {
  _id: string;
  workStatus: 'in progress' | 'incomplete' | 'completed';
  dailyProgress: IDailyProgress[];
}

export interface IOrderDetail {
  _id: string;
  sizeSegmentId: { _id: string; segmentName: string; };
  totalPieces: number;
  groupWorkAssignments: IGroupWorkAssignment[];
}

export interface IVoiceNote {
  _id: string;
  url: string;
  uploadedAt: string;
  [key: string]: any;
}
export interface IAdminNote {
  _id: string;
  userId: { _id: string, fullName: string };
  notes: IVoiceNote[];
}

export interface IOrder {
  _id: string;
  lotNumber: string;
  designId: { basicInfo: { designNo: string; }; };
  orderDetails: IOrderDetail[];
  adminNotes: IAdminNote[];
}


interface GroupWorkState {
  orders: IOrder[];
  groupMembers: GroupMember[]; 
  isLoading: boolean;
  error: string | null;
  fetchTasks: () => Promise<void>;
  claimPieces: (orderId: string, assignmentId: string, memberId: string, memberName: string, piecesToClaim: number) => Promise<void>;
  markTaskComplete: (progressId: string) => Promise<void>;
  updateClaimedPieces: (progressId: string, piecesToClaim: number) => Promise<void>;
}

export const useGroupWorkStore = create<GroupWorkState>()(
  immer((set, get) => ({
    orders: [],
    groupMembers: [], 
    isLoading: true,
    error: null,

    fetchTasks: async () => {
      set({ isLoading: true, error: null });
      try {
        const response = await axiosApi.get('/api/orders/group-work-tasks');

        
  
        const { orders, groupMembers } = response.data.data;
        set({
          orders: orders || [],
          groupMembers: groupMembers || [],
          isLoading: false,
        });
      } catch (err) {
        toast.error("Could not load assigned tasks.");
        set({ isLoading: false, error: "Failed to fetch tasks." });
      }
    },

    claimPieces: async (orderId, assignmentId, memberId, memberName, piecesToClaim) => {
      const toastId = toast.loading("Claiming pieces...");
      try {
        await axiosApi.post(`/api/orders/${orderId}/groups/claim`, {
          assignmentId, memberId, memberName, piecesToClaim,
        });
        toast.success("Pieces claimed!", { id: toastId });
        await get().fetchTasks(); 
      } catch (error: any) {
        toast.error(error.response?.data?.message || "Failed to claim pieces.", { id: toastId });
      }
    },

    markTaskComplete: async (progressId: string) => {
      const toastId = toast.loading("Marking as complete...");
      try {
        await axiosApi.put(`/api/orders/groups/complete/${progressId}`);
        toast.success("Task completed!", { id: toastId });
        await get().fetchTasks();
      } catch (error: any) {
        toast.error(error.response?.data?.message || "Failed to update status.", { id: toastId });
      }
    },
    updateClaimedPieces: async (progressId, piecesToClaim) => {
        const toastId = toast.loading("Updating claim...");
        try {
            await axiosApi.put(`/api/orders/groups/progress/${progressId}`, { piecesToClaim });
            toast.success("Claim updated!", { id: toastId });
            // Data refresh karein
            await get().fetchTasks();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to update claim.", { id: toastId });
            // Error aane par bhi refresh karein taaki state sync mein rahe
            await get().fetchTasks();
        }
    },
  }))
);